import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BasePageLayout from '../components/BasePageLayout';
import { useCountry } from '../context/CountryContext';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { JourneyProgressWidget } from '../components/journey/JourneyProgressWidget';
import { JourneyHomeSection } from '../components/journey/JourneyHomeSection';
import { JourneyApiService } from '../services/journeyApiService';
import { PlatformStatsService } from '../services/platformStatsService';
import { UseCasesSection } from '../components/home/UseCasesSection';
import PageLoader from '../components/common/PageLoader';
import { isAuthInTransition, waitForAuthTransition } from '../utils/sessionValidationUtil';
// import CountrySelector from '../components/ui/CountrySelector';
import './HomePage.css';

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const { availableCountries } = useCountry();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const statsLoadedRef = useRef(false);

  // Loading state for stats only (not blocking page render)
  const [statsLoading, setStatsLoading] = useState(true);

  // User experience mode state
  const [userMode, setUserMode] = useState<'beginner' | 'experienced'>('beginner');
  const [showBeginnerSections, setShowBeginnerSections] = useState(false);
  const [activeJourneyId, setActiveJourneyId] = useState<string | null>(null);

  const [stats, setStats] = useState({
    totalTasks: 0,
    totalTasksPublished: 0,
    tasksWaitingReview: 0,
    tasksRejected: 0,
    tasksApproved: 0,
    totalQuestionnaires: 0,
    totalQuestionnaireCompleted: 0,
    questionnairesWaitingReview: 0,
    questionnairesRejected: 0,
    questionnairesApproved: 0,
    totalTeams: 0,
    teamsWaitingReview: 0,
    teamsRejected: 0,
    teamsApproved: 0,
    totalPreReleaseProjects: 0,
    preReleaseWaitingReview: 0,
    preReleaseRejected: 0,
    preReleaseApproved: 0,
    activeUsers: 0,
    pointsEarned: 0,
    countriesSupported: 0,
    aiIdeasGenerated: 0,
    aiPreReleaseProjects: 0,
    aiLaunchAssets: 0,
    totalAIGenerations: 0
  });

  // Load user mode preference from localStorage
  useEffect(() => {
    const savedMode = localStorage.getItem('homepage_user_mode') as 'beginner' | 'experienced' | null;
    if (savedMode) {
      setUserMode(savedMode);
    }
  }, []);

  // Fetch active journey
  useEffect(() => {
    const fetchActiveJourney = async () => {
      if (!user) return;

      try {
        const response = await JourneyApiService.getUserActiveJourney();
        if (response?.journey) {
          setActiveJourneyId(response.journey.id);
        }
      } catch (error: any) {
        console.error('Error fetching active journey:', error);

        // Check if error is due to expired token
        if (error?.message?.includes('JWT') || error?.message?.includes('expired') || error?.message?.includes('401')) {
          console.log('❌ Token expired while fetching journey, redirecting to login...');
          window.location.href = '/login?session_expired=true';
        }
      }
    };

    fetchActiveJourney();
  }, [user]);

  // Save user mode preference to localStorage
  const toggleUserMode = (mode: 'beginner' | 'experienced') => {
    setUserMode(mode);
    localStorage.setItem('homepage_user_mode', mode);
    setShowBeginnerSections(false); // Reset expanded sections when switching modes
  };

  // Handle startup journey navigation
  const handleStartupJourneyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (activeJourneyId) {
      navigate(`/journey/${activeJourneyId}`);
    } else {
      navigate('/startup-journey/setup');
    }
  };

  // Fetch platform statistics (once per session, non-blocking)
  useEffect(() => {
    // Prevent duplicate calls
    if (statsLoadedRef.current) return;
    statsLoadedRef.current = true;

    const fetchPlatformStats = async () => {
      try {
        setStatsLoading(true);
        
        // Wait for auth transition to complete if in progress (e.g., after SSO login)
        // This prevents validation failures during the brief period when session is being established
        if (isAuthInTransition()) {
          console.log('📊 HomePage: Auth in transition, waiting before fetching stats...');
          await waitForAuthTransition(10000); // Wait up to 10 seconds
        }
        
        // Fetch stats from edge function (uses service_role to bypass RLS)
        // Now with 5-minute caching for instant subsequent loads
        const statsData = await PlatformStatsService.fetchPlatformStatsWithFallback(
          availableCountries.length
        );

        setStats(statsData);

      } catch (error: any) {
        console.error('Error fetching platform stats:', error);

        // Check if error is due to expired token
        if (error?.message?.includes('JWT') || error?.message?.includes('expired') || error?.message?.includes('401')) {
          console.log('❌ Token expired while fetching stats, redirecting to login...');
          window.location.href = '/login?session_expired=true';
          return;
        }

        // Fallback stats are handled by the service
      } finally {
        setStatsLoading(false);
      }
    };

    fetchPlatformStats();
  }, []); // No dependencies - fetch once per mount

  // No longer blocking page render - stats load in background

  return (
    <BasePageLayout background="white" padding="none" maxWidth="none">
      <div className="homepage-container-engaging">
        {/* User Mode Toggle */}
        <div className="user-mode-toggle">
          <div className="mode-toggle-container">
            <span className="mode-label">{t('home.viewMode')}</span>
            <div className="toggle-buttons">
              <button
                className={`toggle-btn ${userMode === 'beginner' ? 'active' : ''}`}
                onClick={() => toggleUserMode('beginner')}
              >
                <i className="bi bi-person-circle"></i>
                {t('home.beginner')}
              </button>
              <button
                className={`toggle-btn ${userMode === 'experienced' ? 'active' : ''}`}
                onClick={() => toggleUserMode('experienced')}
              >
                <i className="bi bi-gear"></i>
                {t('home.experienced')}
              </button>
            </div>
          </div>
        </div>

        {/* Journey Section - Show first for experienced users */}
        {userMode === 'experienced' && !showBeginnerSections && (
          <section className="journey-section-experienced">
            <div className="quick-nav-container">
              <JourneyHomeSection />
            </div>
          </section>
        )}

        {/* Platform Summary Dashboard - Show immediately for experienced users only */}
        {userMode === 'experienced' && !showBeginnerSections && (
          <section className="platform-summary">
            <div className="summary-container">
              <h2 className="summary-title">{t('home.platformOverview')}</h2>
              <p className="summary-subtitle">{t('home.platformOverviewSubtitle')}</p>

              <div className="summary-grid">
                {/* Tasks Summary */}
                <div className="summary-card tasks">
                  <div className="summary-header">
                    <i className="bi bi-list-task"></i>
                    <h3>{t('navigation.tasks')}</h3>
                  </div>
                  <div className="summary-stats">
                    <div className="stat-row">
                      <span className="stat-label">{t("home.published")}</span>
                      <span className="stat-value">{stats.totalTasksPublished.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.waitingReview")}</span>
                      <span className="stat-value warning">{stats.tasksWaitingReview.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.approved")}</span>
                      <span className="stat-value success">{stats.tasksApproved.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.rejected")}</span>
                      <span className="stat-value danger">{stats.tasksRejected.toLocaleString()}</span>
                    </div>
                  </div>
                  <Link to="/tasks" className="summary-action">
                    {t("navigation.browseTasks")} <i className="bi bi-arrow-right"></i>
                  </Link>
                </div>

                {/* Questionnaires Summary */}
                <div className="summary-card questionnaires">
                  <div className="summary-header">
                    <i className="bi bi-clipboard-data"></i>
                    <h3>{t("navigation.surveys")}</h3>
                  </div>
                  <div className="summary-stats">
                    <div className="stat-row">
                      <span className="stat-label">{t("home.available")}</span>
                      <span className="stat-value">{stats.totalQuestionnaires.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.completed")}</span>
                      <span className="stat-value success">{stats.totalQuestionnaireCompleted.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.waitingReview")}</span>
                      <span className="stat-value warning">{stats.questionnairesWaitingReview.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.rejected")}</span>
                      <span className="stat-value danger">{stats.questionnairesRejected.toLocaleString()}</span>
                    </div>
                  </div>
                  <Link to="/questionnaires" className="summary-action">
                    {t("navigation.takeSurveys")} <i className="bi bi-arrow-right"></i>
                  </Link>
                </div>

                {/* Teams Summary */}
                <div className="summary-card teams">
                  <div className="summary-header">
                    <i className="bi bi-people"></i>
                    <h3>{t("navigation.teams")}</h3>
                  </div>
                  <div className="summary-stats">
                    <div className="stat-row">
                      <span className="stat-label">{t("home.totalTeams")}</span>
                      <span className="stat-value">{stats.totalTeams.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.waitingReview")}</span>
                      <span className="stat-value warning">{stats.teamsWaitingReview.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.approved")}</span>
                      <span className="stat-value success">{stats.teamsApproved.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.rejected")}</span>
                      <span className="stat-value danger">{stats.teamsRejected.toLocaleString()}</span>
                    </div>
                  </div>
                  <Link to="/teams" className="summary-action">
                    {t("navigation.joinTeam")} <i className="bi bi-arrow-right"></i>
                  </Link>
                </div>

                {/* Pre-Release Projects Summary */}
                <div className="summary-card prerelease">
                  <div className="summary-header">
                    <i className="bi bi-rocket"></i>
                    <h3>{t("navigation.preRelease")}</h3>
                  </div>
                  <div className="summary-stats">
                    <div className="stat-row">
                      <span className="stat-label">{t("home.totalProjects")}</span>
                      <span className="stat-value">{stats.totalPreReleaseProjects.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.waitingReview")}</span>
                      <span className="stat-value warning">{stats.preReleaseWaitingReview.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.approved")}</span>
                      <span className="stat-value success">{stats.preReleaseApproved.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.rejected")}</span>
                      <span className="stat-value danger">{stats.preReleaseRejected.toLocaleString()}</span>
                    </div>
                  </div>
                  <Link to="/pre-release" className="summary-action">
                    {t("navigation.browseProjects")} <i className="bi bi-arrow-right"></i>
                  </Link>
                </div>
                {/* AI-Generated Content Summary */}
                <div className="summary-card ai-generated">
                  <div className="summary-header">
                    <i className="bi bi-stars"></i>
                    <h3>{t("aiGenerated.title")}</h3>
                  </div>
                  <div className="summary-stats">
                    <div className="stat-row">
                      <span className="stat-label">{t("aiGenerated.ideasGenerated")}</span>
                      <span className="stat-value">{stats.aiIdeasGenerated.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("aiGenerated.preReleaseProjects")}</span>
                      <span className="stat-value">{stats.aiPreReleaseProjects.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("aiGenerated.launchAssets")}</span>
                      <span className="stat-value">{stats.aiLaunchAssets.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("aiGenerated.totalGenerations")}</span>
                      <span className="stat-value success">{stats.totalAIGenerations.toLocaleString()}</span>
                    </div>
                  </div>
                  <Link to="/startup-journey/setup" className="summary-action">
                    {t("aiGenerated.exploreAI")} <i className="bi bi-arrow-right"></i>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Experienced User: Show/Hide Beginner Sections Toggle */}
        {userMode === 'experienced' && !showBeginnerSections && (
          <section className="toggle-sections">
            <div className="toggle-sections-container">
              <button
                className="show-beginner-sections-btn"
                onClick={() => setShowBeginnerSections(true)}
              >
                <i className="bi bi-eye"></i>
                {t('home.viewBeginnerGuide')}
              </button>
            </div>
          </section>
        )}

        {/* Hero Section with Clear Value Proposition - Only show for beginners */}
        {(userMode === 'beginner' || showBeginnerSections) && (
          <section className="hero-section-engaging">{/* ...existing code... */}
            <div className="hero-content">
              <div className="hero-badge">
                🎯 <span>{t('home.freeForever')}</span>
              </div>
              <h1 className="hero-title-engaging">
                {t('home.heroTitle')}
              </h1>
              <h1 className="hero-title-highlight">{t('home.heroTitleHighlight')}</h1>
              <p className="hero-subtitle-engaging">
                {t('home.heroSubtitle')}<br />
                {t('home.heroSubtitleDetail')}
              </p>

              {/* Platform Value Points */}
              <div className="value-points">
                <div className="value-point">
                  <i className="bi bi-check-circle-fill"></i>
                  <span>{t('home.freeForever')}</span>
                </div>
                <div className="value-point">
                  <i className="bi bi-arrow-repeat"></i>
                  <span>{t('home.helpOthersGetHelp')}</span>
                </div>
                <div className="value-point">
                  <i className="bi bi-trophy"></i>
                  <span>{t('home.earnPoints')}</span>
                </div>
              </div>

              {/* Live Platform Stats */}
              <div className="platform-stats">
                <div className="stat-live-item">
                  <span className="stat-number">{stats.totalTasks.toLocaleString()}</span>
                  <span className="stat-label">{t('home.tasksPublished')}</span>
                </div>
                <div className="stat-live-item">
                  <span className="stat-number">{stats.totalQuestionnaires.toLocaleString()}</span>
                  <span className="stat-label">{t('home.surveysCompleted')}</span>
                </div>
                <div className="stat-live-item">
                  <span className="stat-number">{stats.activeUsers.toLocaleString()}</span>
                  <span className="stat-label">{t('home.activeMembers')}</span>
                </div>
                <div className="stat-live-item">
                  <span className="stat-number">{stats.pointsEarned.toLocaleString()}</span>
                  <span className="stat-label">{t('home.pointsEarned')}</span>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* How It Works Section - Conditional */}
        {(userMode === 'beginner' || showBeginnerSections) && (
          <section className="quick-nav-section how-it-works-styled">
            <div className="quick-nav-container">
              <h2 className="quick-nav-title">{t('home.howItWorks')}</h2>
              <p className="quick-nav-subtitle">{t('home.howItWorksSubtitle')}</p>

              <div className="navigation-grid steps-navigation-grid">
                <div className="nav-category step-category">
                  <div className="category-header">
                    <h3><span className="step-number-inline">1</span>{t('home.step1Title')}</h3>
                  </div>
                  <div className="step-description">
                    <p>{t('home.step1Description')}</p>
                  </div>
                  <div className="nav-options">
                    <div className="nav-option step-option">
                      <i className="bi bi-list-task"></i>
                      <div>
                        <span className="option-title">{t('home.step1Option1')}</span>
                        <span className="option-desc">{t('home.step1Option1Desc')}</span>
                      </div>
                    </div>
                    <div className="nav-option step-option">
                      <i className="bi bi-clipboard-data"></i>
                      <div>
                        <span className="option-title">{t('home.step1Option2')}</span>
                        <span className="option-desc">{t('home.step1Option2Desc')}</span>
                      </div>
                    </div>
                    <div className="nav-option step-option">
                      <i className="bi bi-people"></i>
                      <div>
                        <span className="option-title">{t('home.step1Option3')}</span>
                        <span className="option-desc">{t('home.step1Option3Desc')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="nav-category step-category">
                  <div className="category-header">
                    <h3><span className="step-number-inline">2</span>{t('home.step2Title')}</h3>
                  </div>
                  <div className="step-description">
                    <p>{t('home.step2Description')}</p>
                  </div>
                  <div className="nav-options">
                    <div className="nav-option step-option">
                      <i className="bi bi-trophy"></i>
                      <div>
                        <span className="option-title">{t('home.step2Option1')}</span>
                        <span className="option-desc">{t('home.step2Option1Desc')}</span>
                      </div>
                    </div>
                    <div className="nav-option step-option">
                      <i className="bi bi-star"></i>
                      <div>
                        <span className="option-title">{t('home.step2Option2')}</span>
                        <span className="option-desc">{t('home.step2Option2Desc')}</span>
                      </div>
                    </div>
                    <div className="nav-option step-option">
                      <i className="bi bi-award"></i>
                      <div>
                        <span className="option-title">{t('home.step2Option3')}</span>
                        <span className="option-desc">{t('home.step2Option3Desc')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="nav-category step-category">
                  <div className="category-header">
                    <h3><span className="step-number-inline">3</span>{t('home.step3Title')}</h3>
                  </div>
                  <div className="step-description">
                    <p>{t('home.step3Description')}</p>
                  </div>
                  <div className="nav-options">
                    <div className="nav-option step-option">
                      <i className="bi bi-plus-circle"></i>
                      <div>
                        <span className="option-title">{t('home.step3Option1')}</span>
                        <span className="option-desc">{t('home.step3Option1Desc')}</span>
                      </div>
                    </div>
                    <div className="nav-option step-option">
                      <i className="bi bi-graph-up"></i>
                      <div>
                        <span className="option-title">{t('home.step3Option2')}</span>
                        <span className="option-desc">{t('home.step3Option2Desc')}</span>
                      </div>
                    </div>
                    <div className="nav-option step-option">
                      <i className="bi bi-chat-dots"></i>
                      <div>
                        <span className="option-title">{t('home.step3Option3')}</span>
                        <span className="option-desc">{t('home.step3Option3Desc')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>


            </div>
          </section>
        )}

        {/* Use Cases Section - Conditional */}
        {(userMode === 'beginner' || showBeginnerSections) && (
          <UseCasesSection activeJourneyId={activeJourneyId} />)}

        {/* Quick Navigation for Beginners - Conditional */}
        {(userMode === 'beginner' || showBeginnerSections) && (
          <section className="quick-nav-section">
            <div className="quick-nav-container">
              <h2 className="quick-nav-title">{t('home.whatDoYouWantToDo')}</h2>
              <p className="quick-nav-subtitle">{t('home.whatDoYouWantToDoSubtitle')}</p>

              <div className="navigation-grid">
                {/* Build & Launch Section - FIRST ROW */}
                <div className="nav-category build">
                  <div className="category-header">
                    <i className="bi bi-rocket-takeoff-fill"></i>
                    <h3>{t('home.iBuildAndLaunch')}</h3>
                    <p>{t('home.iBuildAndLaunchDesc')}</p>
                  </div>
                  <div className="nav-options">
                    <a href="#" onClick={handleStartupJourneyClick} className="nav-option primary">
                      <i className="bi bi-map"></i>
                      <div>
                        <span className="option-title">{t('home.startYourJourney')}</span>
                        <span className="option-desc">{t('home.startYourJourneyDesc')}</span>
                      </div>
                    </a>
                    <Link to="/create-pre-release-project" className="nav-option">
                      <i className="bi bi-rocket-takeoff"></i>
                      <div>
                        <span className="option-title">{t('home.testMarketInterest')}</span>
                        <span className="option-desc">{t('home.testMarketInterestDesc')}</span>
                      </div>
                    </Link>
                    <Link to="/publisher-dashboard" className="nav-option">
                      <i className="bi bi-graph-up-arrow"></i>
                      <div>
                        <span className="option-title">{t("home.publisherDashboard")}</span>
                        <span className="option-desc">{t("home.publisherDashboardDesc")}</span>
                      </div>
                    </Link>
                    <Link to="/journey-list" className="nav-option">
                      <i className="bi bi-list-check"></i>
                      <div>
                        <span className="option-title">{t("home.myJourney")}</span>
                        <span className="option-desc">{t("home.myJourneyDesc")}</span>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Need Points Section - SECOND ROW */}
                <div className="nav-category need-help">
                  <div className="category-header">
                    <i className="bi bi-star-fill"></i>
                    <h3>{t('home.iNeedPoints')}</h3>
                    <p>{t('home.iNeedPointsDesc')}</p>
                  </div>
                  <div className="nav-options">
                    <Link to="/tasks/task-taker" className="nav-option primary">
                      <i className="bi bi-check-square"></i>
                      <div>
                        <span className="option-title">{t('home.completeTasks')}</span>
                        <span className="option-desc">{t('home.completeTasksDesc')}</span>
                      </div>
                    </Link>
                    <Link to="/questionnaires" className="nav-option">
                      <i className="bi bi-clipboard-data"></i>
                      <div>
                        <span className="option-title">{t('home.takeSurveys')}</span>
                        <span className="option-desc">{t('home.takeSurveysDesc')}</span>
                      </div>
                    </Link>
                    <Link to="/teams/join" className="nav-option">
                      <i className="bi bi-person-plus"></i>
                      <div>
                        <span className="option-title">{t('home.joinTeams')}</span>
                        <span className="option-desc">{t('home.joinTeamsDesc')}</span>
                      </div>
                    </Link>
                    <Link to="/claim-bonuses" className="nav-option">
                      <i className="bi bi-gift"></i>
                      <div>
                        <span className="option-title">{t('home.claimBonuses')}</span>
                        <span className="option-desc">{t('home.claimBonusesDesc')}</span>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Need Ideas Section - SECOND ROW */}
                <div className="nav-category contribute">
                  <div className="category-header">
                    <i className="bi bi-lightbulb-fill"></i>
                    <h3>{t('home.iNeedIdeas')}</h3>
                    <p>{t('home.iNeedIdeasDesc')}</p>
                  </div>

                  <div className="nav-options">
                    <a href="#" onClick={handleStartupJourneyClick} className="nav-option primary">
                      <i className="bi bi-rocket-takeoff"></i>
                      <div>
                        <span className="option-title">{t('home.startupJourney')}</span>
                        <span className="option-desc">{t('home.startupJourneyDesc')}</span>
                      </div>
                    </a>
                    <Link to="/create-questionnaire" className="nav-option">
                      <i className="bi bi-ui-checks"></i>
                      <div>
                        <span className="option-title">{t('home.createSurvey')}</span>
                        <span className="option-desc">{t('home.createSurveyDesc')}</span>
                      </div>
                    </Link>
                    <Link to="/forums" className="nav-option">
                      <i className="bi bi-chat-dots"></i>
                      <div>
                        <span className="option-title">{t('home.browseForums')}</span>
                        <span className="option-desc">{t('home.browseForumsDesc')}</span>
                      </div>
                    </Link>
                    <Link to="/pre-release" className="nav-option">
                      <i className="bi bi-eye"></i>
                      <div>
                        <span className="option-title">{t('home.seeOtherProjects')}</span>
                        <span className="option-desc">{t('home.seeOtherProjectsDesc')}</span>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Need Help Section - SECOND ROW */}
                <div className="nav-category learn">
                  <div className="category-header">
                    <i className="bi bi-question-circle-fill"></i>
                    <h3>{t('home.iNeedHelp')}</h3>
                    <p>{t('home.iNeedHelpDesc')}</p>
                  </div>
                  <div className="nav-options">
                    <Link to="/create-task" className="nav-option primary">
                      <i className="bi bi-clipboard-plus"></i>
                      <div>
                        <span className="option-title">{t('home.publishTask')}</span>
                        <span className="option-desc">{t('home.publishTaskDesc')}</span>
                      </div>
                    </Link>
                    <Link to="/create-team-request" className="nav-option">
                      <i className="bi bi-people-fill"></i>
                      <div>
                        <span className="option-title">{t('home.findTeamMembers')}</span>
                        <span className="option-desc">{t('home.findTeamMembersDesc')}</span>
                      </div>
                    </Link>
                    <Link to="/tasks/publisher" className="nav-option">
                      <i className="bi bi-briefcase"></i>
                      <div>
                        <span className="option-title">{t('home.myTasks')}</span>
                        <span className="option-desc">{t('home.myTasksDesc')}</span>
                      </div>
                    </Link>
                    <Link to="/messages" className="nav-option">
                      <i className="bi bi-envelope"></i>
                      <div>
                        <span className="option-title">{t('home.messages')}</span>
                        <span className="option-desc">{t('home.messagesDesc')}</span>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Journey Home Section - After all categories for beginner mode */}
              <JourneyHomeSection />
            </div>
          </section>
        )}

        {/* Platform Summary Dashboard - Show at the end for beginner users */}
        {userMode === 'beginner' && (
          <section className="platform-summary">
            <div className="summary-container">
              <h2 className="summary-title">{t("home.platformOverview")}</h2>
              <p className="summary-subtitle">{t("home.platformOverviewSubtitle")}</p>

              <div className="summary-grid">
                {/* Tasks Summary */}
                <div className="summary-card tasks">
                  <div className="summary-header">
                    <i className="bi bi-list-task"></i>
                    <h3>{t("navigation.tasks")}</h3>
                  </div>
                  <div className="summary-stats">
                    <div className="stat-row">
                      <span className="stat-label">{t("home.published")}</span>
                      <span className="stat-value">{stats.totalTasksPublished.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.waitingReview")}</span>
                      <span className="stat-value warning">{stats.tasksWaitingReview.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.approved")}</span>
                      <span className="stat-value success">{stats.tasksApproved.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.rejected")}</span>
                      <span className="stat-value danger">{stats.tasksRejected.toLocaleString()}</span>
                    </div>
                  </div>
                  <Link to="/tasks" className="summary-action">
                    {t("navigation.browseTasks")} <i className="bi bi-arrow-right"></i>
                  </Link>
                </div>

                {/* Questionnaires Summary */}
                <div className="summary-card questionnaires">
                  <div className="summary-header">
                    <i className="bi bi-clipboard-data"></i>
                    <h3>{t("navigation.surveys")}</h3>
                  </div>
                  <div className="summary-stats">
                    <div className="stat-row">
                      <span className="stat-label">{t("home.available")}</span>
                      <span className="stat-value">{stats.totalQuestionnaires.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.completed")}</span>
                      <span className="stat-value success">{stats.totalQuestionnaireCompleted.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.waitingReview")}</span>
                      <span className="stat-value warning">{stats.questionnairesWaitingReview.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.rejected")}</span>
                      <span className="stat-value danger">{stats.questionnairesRejected.toLocaleString()}</span>
                    </div>
                  </div>
                  <Link to="/questionnaires" className="summary-action">
                    {t("navigation.takeSurveys")} <i className="bi bi-arrow-right"></i>
                  </Link>
                </div>

                {/* Teams Summary */}
                <div className="summary-card teams">
                  <div className="summary-header">
                    <i className="bi bi-people"></i>
                    <h3>{t("navigation.teams")}</h3>
                  </div>
                  <div className="summary-stats">
                    <div className="stat-row">
                      <span className="stat-label">{t("home.totalTeams")}</span>
                      <span className="stat-value">{stats.totalTeams.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.waitingReview")}</span>
                      <span className="stat-value warning">{stats.teamsWaitingReview.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.approved")}</span>
                      <span className="stat-value success">{stats.teamsApproved.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.rejected")}</span>
                      <span className="stat-value danger">{stats.teamsRejected.toLocaleString()}</span>
                    </div>
                  </div>
                  <Link to="/teams" className="summary-action">
                    {t("navigation.joinTeam")} <i className="bi bi-arrow-right"></i>
                  </Link>
                </div>

                {/* Pre-Release Projects Summary */}
                <div className="summary-card prerelease">
                  <div className="summary-header">
                    <i className="bi bi-rocket"></i>
                    <h3>{t("navigation.preRelease")}</h3>
                  </div>
                  <div className="summary-stats">
                    <div className="stat-row">
                      <span className="stat-label">{t("home.totalProjects")}</span>
                      <span className="stat-value">{stats.totalPreReleaseProjects.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.waitingReview")}</span>
                      <span className="stat-value warning">{stats.preReleaseWaitingReview.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.approved")}</span>
                      <span className="stat-value success">{stats.preReleaseApproved.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.rejected")}</span>
                      <span className="stat-value danger">{stats.preReleaseRejected.toLocaleString()}</span>
                    </div>
                  </div>
                  <Link to="/pre-release" className="summary-action">
                    {t("navigation.browseProjects")} <i className="bi bi-arrow-right"></i>
                  </Link>
                </div>

                {/* AI-Generated Content Summary */}
                <div className="summary-card ai-generated">
                  <div className="summary-header">
                    <i className="bi bi-stars"></i>
                    <h3>{t("aiGenerated.title")}</h3>
                  </div>
                  <div className="summary-stats">
                    <div className="stat-row">
                      <span className="stat-label">{t("aiGenerated.ideasGenerated")}</span>
                      <span className="stat-value">{stats.aiIdeasGenerated.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("aiGenerated.preReleaseProjects")}</span>
                      <span className="stat-value">{stats.aiPreReleaseProjects.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("aiGenerated.launchAssets")}</span>
                      <span className="stat-value">{stats.aiLaunchAssets.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("aiGenerated.totalGenerations")}</span>
                      <span className="stat-value success">{stats.totalAIGenerations.toLocaleString()}</span>
                    </div>
                  </div>
                  <Link to="/startup-journey/setup" className="summary-action">
                    {t("aiGenerated.exploreAI")} <i className="bi bi-arrow-right"></i>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Platform Summary Dashboard - Show at the end when experienced users expand beginner sections */}
        {userMode === 'experienced' && showBeginnerSections && (
          <section className="platform-summary">
            <div className="summary-container">
              <h2 className="summary-title">📊 Platform Activity Overview</h2>
              <p className="summary-subtitle">Real-time statistics of what's happening on our platform</p>

              <div className="summary-grid">
                {/* Tasks Summary */}
                <div className="summary-card tasks">
                  <div className="summary-header">
                    <i className="bi bi-list-task"></i>
                    <h3>{t("navigation.tasks")}</h3>
                  </div>
                  <div className="summary-stats">
                    <div className="stat-row">
                      <span className="stat-label">{t("home.published")}</span>
                      <span className="stat-value">{stats.totalTasksPublished.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.waitingReview")}</span>
                      <span className="stat-value warning">{stats.tasksWaitingReview.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.approved")}</span>
                      <span className="stat-value success">{stats.tasksApproved.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.rejected")}</span>
                      <span className="stat-value danger">{stats.tasksRejected.toLocaleString()}</span>
                    </div>
                  </div>
                  <Link to="/tasks" className="summary-action">
                    {t("navigation.browseTasks")} <i className="bi bi-arrow-right"></i>
                  </Link>
                </div>

                {/* Questionnaires Summary */}
                <div className="summary-card questionnaires">
                  <div className="summary-header">
                    <i className="bi bi-clipboard-data"></i>
                    <h3>{t("navigation.surveys")}</h3>
                  </div>
                  <div className="summary-stats">
                    <div className="stat-row">
                      <span className="stat-label">{t("home.available")}</span>
                      <span className="stat-value">{stats.totalQuestionnaires.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.completed")}</span>
                      <span className="stat-value success">{stats.totalQuestionnaireCompleted.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.waitingReview")}</span>
                      <span className="stat-value warning">{stats.questionnairesWaitingReview.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.rejected")}</span>
                      <span className="stat-value danger">{stats.questionnairesRejected.toLocaleString()}</span>
                    </div>
                  </div>
                  <Link to="/questionnaires" className="summary-action">
                    {t("navigation.takeSurveys")} <i className="bi bi-arrow-right"></i>
                  </Link>
                </div>

                {/* Teams Summary */}
                <div className="summary-card teams">
                  <div className="summary-header">
                    <i className="bi bi-people"></i>
                    <h3>{t("navigation.teams")}</h3>
                  </div>
                  <div className="summary-stats">
                    <div className="stat-row">
                      <span className="stat-label">{t("home.totalTeams")}</span>
                      <span className="stat-value">{stats.totalTeams.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.waitingReview")}</span>
                      <span className="stat-value warning">{stats.teamsWaitingReview.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.approved")}</span>
                      <span className="stat-value success">{stats.teamsApproved.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.rejected")}</span>
                      <span className="stat-value danger">{stats.teamsRejected.toLocaleString()}</span>
                    </div>
                  </div>
                  <Link to="/teams" className="summary-action">
                    {t("navigation.joinTeam")} <i className="bi bi-arrow-right"></i>
                  </Link>
                </div>

                {/* Pre-Release Projects Summary */}
                <div className="summary-card prerelease">
                  <div className="summary-header">
                    <i className="bi bi-rocket"></i>
                    <h3>{t("navigation.preRelease")}</h3>
                  </div>
                  <div className="summary-stats">
                    <div className="stat-row">
                      <span className="stat-label">{t("home.totalProjects")}</span>
                      <span className="stat-value">{stats.totalPreReleaseProjects.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.waitingReview")}</span>
                      <span className="stat-value warning">{stats.preReleaseWaitingReview.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.approved")}</span>
                      <span className="stat-value success">{stats.preReleaseApproved.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("home.rejected")}</span>
                      <span className="stat-value danger">{stats.preReleaseRejected.toLocaleString()}</span>
                    </div>
                  </div>
                  <Link to="/pre-release" className="summary-action">
                    {t("navigation.browseProjects")} <i className="bi bi-arrow-right"></i>
                  </Link>
                </div>

                {/* AI-Generated Content Summary */}
                <div className="summary-card ai-generated">
                  <div className="summary-header">
                    <i className="bi bi-stars"></i>
                    <h3>{t("aiGenerated.title")}</h3>
                  </div>
                  <div className="summary-stats">
                    <div className="stat-row">
                      <span className="stat-label">{t("aiGenerated.ideasGenerated")}</span>
                      <span className="stat-value">{stats.aiIdeasGenerated.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("aiGenerated.preReleaseProjects")}</span>
                      <span className="stat-value">{stats.aiPreReleaseProjects.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("aiGenerated.launchAssets")}</span>
                      <span className="stat-value">{stats.aiLaunchAssets.toLocaleString()}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">{t("aiGenerated.totalGenerations")}</span>
                      <span className="stat-value success">{stats.totalAIGenerations.toLocaleString()}</span>
                    </div>
                  </div>
                  <Link to="/startup-journey/setup" className="summary-action">
                    {t("aiGenerated.exploreAI")} <i className="bi bi-arrow-right"></i>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </BasePageLayout>
  );
};

export default HomePage;
